import { Controller } from "@hotwired/stimulus"

const DEBOUNCE_MS = 350

// Formulaire « Créer une personne » : doublons pendant la frappe (frame people_quick_duplicates),
// recherche et ajout de rattachements (cartes clonées depuis un <template>), création bloquée tant
// qu'un doublon n'est ni ouvert ni écarté. Sans cible « duplicates » (panneau « Rattacher » d'une
// fiche existante), seuls les rattachements sont gérés.
export default class extends Controller {
  static targets = ["firstName", "lastName", "email", "phone", "duplicates", "dismissed", "attachments", "search",
                    "cardTemplate", "card"]
  static values = { duplicatesUrl: String, searchUrl: String }

  connect() {
    if (!this.hasDuplicatesTarget) return
    this.onFrameLoad = (event) => { if (event.target === this.duplicatesTarget) this.updateSubmit() }
    document.addEventListener("turbo:frame-load", this.onFrameLoad)
    this.updateSubmit()
  }

  disconnect() {
    if (this.onFrameLoad) document.removeEventListener("turbo:frame-load", this.onFrameLoad)
    clearTimeout(this.duplicatesTimer)
    clearTimeout(this.searchTimer)
  }

  checkDuplicates() {
    if (!this.hasDuplicatesTarget) return
    clearTimeout(this.duplicatesTimer)
    this.duplicatesTimer = setTimeout(() => this.refreshDuplicates(), DEBOUNCE_MS)
  }

  refreshDuplicates() {
    const params = new URLSearchParams({
      kind: "natural",
      first_name: this.firstNameTarget.value,
      last_name: this.lastNameTarget.value,
      email: this.emailTarget.value,
      phone: this.phoneTarget.value
    })
    this.dismissedIds().forEach((id) => params.append("dismissed[]", id))
    if (!this.lastNameTarget.value.trim() && !this.emailTarget.value.trim() && !this.phoneTarget.value.trim()) {
      this.duplicatesTarget.innerHTML = ""
      this.updateSubmit()
      return
    }
    this.duplicatesTarget.src = `${this.duplicatesUrlValue}?${params}`
  }

  dismiss(event) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "person[dismissed_duplicate_ids][]"
    input.value = event.currentTarget.dataset.personId
    this.dismissedTarget.appendChild(input)
    this.refreshDuplicates()
  }

  dismissedIds() {
    return Array.from(this.dismissedTarget.querySelectorAll("input")).map((input) => input.value)
  }

  updateSubmit() {
    const blocking = this.duplicatesTarget.querySelector("[data-blocking='true']")
    const form = this.element
    document.querySelectorAll(`button[form='${form.id}'][data-people-quick-submit]`).forEach((button) => {
      button.disabled = Boolean(blocking)
    })
  }

  openSearch(event) {
    const type = event.currentTarget.dataset.type
    this.searchTargets.forEach((block) => {
      block.hidden = block.dataset.type !== type || !block.hidden
      if (!block.hidden) block.querySelector("input")?.focus()
    })
  }

  search(event) {
    const input = event.currentTarget
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      const frame = document.getElementById(`people_quick_attachment_results_${input.dataset.type}`)
      const params = new URLSearchParams({ type: input.dataset.type, q: input.value })
      frame.src = `${this.searchUrlValue}?${params}`
    }, DEBOUNCE_MS)
  }

  attach(event) {
    const data = event.currentTarget.dataset
    const block = this.searchTargets.find((element) => element.dataset.type === data.type)
    const roles = JSON.parse(block.dataset.roles)
    const index = Date.now()
    const html = this.cardTemplateTarget.innerHTML
      .replaceAll("__INDEX__", index)
      .replaceAll("__TYPE__", data.type)
    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()
    const card = wrapper.firstElementChild
    const set = (field, value) => { card.querySelector(`[data-card-field='${field}']`).value = value || "" }
    set("type", data.type)
    set("id", data.id)
    set("name", data.name)
    set("label", data.label)
    set("meta", data.meta)
    card.querySelector("[data-card-label]").textContent = data.label
    card.querySelector("[data-card-meta]").textContent = data.meta || ""
    const icon = { household: "fa-house-user", indivision: "fa-people-roof", company: "fa-building" }[data.type]
    card.querySelector("[data-card-icon]").className = `fas ${icon} text-primary`
    const select = card.querySelector("[data-card-field='role']")
    select.innerHTML = roles.map(([value, label]) => `<option value="${value}">${label}</option>`).join("")
    card.querySelector("[data-card-field='share']").hidden = data.type !== "indivision"
    this.attachmentsTarget.appendChild(card)
    block.hidden = true
    block.querySelector("input").value = ""
    document.getElementById(`people_quick_attachment_results_${data.type}`).innerHTML = ""
  }

  detach(event) {
    event.currentTarget.closest("[data-people--quick-person-target='card']").remove()
  }
}
